import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import { WORKY_BRAND_LOGO_SRC } from '../common/WorkyBrandConstants';
import { isValidString } from '../common/CommonUtilsMethods';
import {
    buildWorkyAuthPathWithEmail,
    readWorkyAuthEmailFromLocationSearch,
} from './WorkyAuthQueryParamService';
import {
    confirmSignUpWithCode,
    resendSignUpConfirmationCode,
} from './WorkyCognitoAuthService';
import type { CognitoSignUpFailureResult } from './WorkyCognitoAuthServiceInterfaces';
import { useWorkyVerificationResendCooldown } from './WorkyVerificationResendCooldownHook';
import { VerificationResendCooldownScope } from './WorkyVerificationResendCooldownInterfaces';
import './WorkyLoginPage.css';

const WorkyVerifyEmailPage = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const initialEmailAddress: string = useMemo(
        (): string => readWorkyAuthEmailFromLocationSearch(location.search),
        [location.search]
    );
    const [emailAddress, setEmailAddress] = useState<string>(initialEmailAddress);
    const [confirmationCode, setConfirmationCode] = useState<string>('');
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [isResendingCode, setIsResendingCode] = useState<boolean>(false);

    const {
        isResendCooldownActive,
        resendCooldownSecondsRemaining,
        beginResendCooldown,
        formatResendButtonLabel,
    } = useWorkyVerificationResendCooldown({
        cooldownScope: VerificationResendCooldownScope.VERIFY_EMAIL,
        emailAddress,
    });

    const handleNavigateToLogin = useCallback((): void => {
        history.push(
            isValidString(emailAddress)
                ? buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.login, emailAddress)
                : WORKY_WEB_APP_PATHS.login
        );
    }, [emailAddress, history]);

    const handleResendCode = useCallback(async (): Promise<void> => {
        const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
        if (isResendingCode || !isValidString(normalizedEmailAddress)) {
            if (!isValidString(normalizedEmailAddress)) {
                enqueueSnackbar('Please enter your email address.', { variant: 'warning' });
            }
            return;
        }

        if (isResendCooldownActive) {
            enqueueSnackbar(
                `Please wait ${resendCooldownSecondsRemaining} seconds before resending.`,
                { variant: 'warning' }
            );
            return;
        }

        setIsResendingCode(true);
        try {
            const resendFailure: CognitoSignUpFailureResult | null =
                await resendSignUpConfirmationCode(normalizedEmailAddress);
            if (resendFailure !== null) {
                enqueueSnackbar(resendFailure.errorMessage, { variant: 'error' });
                return;
            }
            setEmailAddress(normalizedEmailAddress);
            beginResendCooldown();
            enqueueSnackbar('A new verification code was sent to your email.', {
                variant: 'success',
            });
        } finally {
            setIsResendingCode(false);
        }
    }, [
        beginResendCooldown,
        emailAddress,
        enqueueSnackbar,
        isResendCooldownActive,
        isResendingCode,
        resendCooldownSecondsRemaining,
    ]);

    const handleSubmit = useCallback(
        async (submitEvent: React.FormEvent<HTMLFormElement>): Promise<void> => {
            submitEvent.preventDefault();
            if (isVerifying) {
                return;
            }

            const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
            if (!isValidString(normalizedEmailAddress) || !isValidString(confirmationCode)) {
                enqueueSnackbar('Email and verification code are required.', { variant: 'warning' });
                return;
            }

            setIsVerifying(true);
            try {
                const confirmFailure: CognitoSignUpFailureResult | null =
                    await confirmSignUpWithCode({
                        emailAddress: normalizedEmailAddress,
                        confirmationCode,
                    });
                if (confirmFailure !== null) {
                    enqueueSnackbar(confirmFailure.errorMessage, { variant: 'error' });
                    return;
                }

                enqueueSnackbar('Email verified. Please sign in to continue registration.', {
                    variant: 'success',
                });
                history.replace(
                    buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.login, normalizedEmailAddress)
                );
            } finally {
                setIsVerifying(false);
            }
        },
        [
            confirmationCode,
            emailAddress,
            enqueueSnackbar,
            history,
            isVerifying,
        ]
    );

    return (
        <div className="worky-login-page">
            <button
                type="button"
                className="worky-login-back-home-button"
                onClick={handleNavigateToLogin}
            >
                <ArrowBackIcon fontSize="small" />
                Back to sign in
            </button>
            <div className="worky-login-card">
                <button
                    type="button"
                    className="worky-login-logo-button"
                    onClick={handleNavigateToLogin}
                    aria-label="Worky sign in"
                >
                    <img src={WORKY_BRAND_LOGO_SRC} alt="Worky" className="worky-login-logo-image" />
                </button>

                <h1 className="worky-login-title">Verify email</h1>
                <p className="worky-login-subtitle">
                    Your account was created, but your email still needs to be verified before you
                    can reset your password or finish signing in.
                </p>

                <form className="worky-login-form" onSubmit={handleSubmit}>
                    <TextField
                        label="Email"
                        type="email"
                        autoComplete="email"
                        value={emailAddress}
                        onChange={(changeEvent): void => setEmailAddress(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Verification code"
                        autoComplete="one-time-code"
                        value={confirmationCode}
                        onChange={(changeEvent): void => setConfirmationCode(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="worky-login-submit-button"
                        disabled={isVerifying || isResendingCode}
                        fullWidth
                    >
                        {isVerifying ? <CircularProgress size={24} color="inherit" /> : 'Verify email'}
                    </Button>
                    <Button
                        type="button"
                        variant="text"
                        disabled={isVerifying || isResendingCode || isResendCooldownActive}
                        fullWidth
                        onClick={(): void => {
                            void handleResendCode();
                        }}
                    >
                        {isResendingCode
                            ? 'Sending code…'
                            : formatResendButtonLabel('Send verification code')}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export { WorkyVerifyEmailPage };
